import React, { useEffect, useRef } from 'react';
import { fadeIn } from 'react-animations';
import { StyleSheet, css } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBriefcase,
  faEnvelope,
  faMapMarkerAlt,
  faDownload,
  faGraduationCap,
  faBook,
  faUsers,
  faCode,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';
import { faLinkedin, faGoogleScholar } from '@fortawesome/free-brands-svg-icons';
import { skillsAndMethods } from '../data/projects';

function Resume() {
  const observerRef = useRef();

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    const elements = document.querySelectorAll('[data-animate-id]');
    elements.forEach(el => {
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const styles = StyleSheet.create({
    fadeIn: {
      opacity: 0,
      animationName: fadeIn,
      animationDuration: '1s',
      animationFillMode: 'forwards',
    },
    sectionWrapper: {
      padding: '6rem 0',
      fontFamily: "'Lato', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '@media print': {
        padding: 0,
        backgroundColor: 'white',
        display: 'block',
      }
    },
    titleBox: {
      textAlign: 'center',
      marginBottom: '2rem',
      width: '100%',
      '@media print': {
        display: 'none',
      }
    },
    mainTitle: {
      fontSize: '2.5rem',
      fontWeight: '700',
      marginBottom: '1rem',
      color: 'var(--text-primary)',
      letterSpacing: '-0.025em',
      '@media (min-width: 768px)': {
        fontSize: '3rem',
      }
    },
    titleUnderline: {
      width: '4rem',
      height: '0.25rem',
      backgroundColor: 'var(--primary)',
      margin: '0 auto',
      borderRadius: '9999px',
    },
    paperContainer: {
      backgroundColor: 'var(--bg-card)',
      color: 'var(--text-primary)',
      width: '100%',
      maxWidth: '1100px',
      minHeight: '297mm',
      margin: '0 auto',
      padding: '4rem 5rem',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      boxSizing: 'border-box',
      '@media (max-width: 900px)': {
        maxWidth: '95%',
        padding: '3rem 2rem',
        minHeight: 'auto',
        height: 'auto',
      },
      '@media print': {
        boxShadow: 'none',
        margin: 0,
        padding: '0',
        width: '100%',
        maxWidth: '100%',
        minHeight: 'auto',
        backgroundColor: 'white',
        color: 'black',      }
    },
    topLabel: {
      position: 'absolute',
      top: '2rem',
      right: '3rem',
      fontSize: '0.75rem',
      fontWeight: '700',
      letterSpacing: '0.2em',
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      opacity: 0.5,
      '@media (max-width: 768px)': {
        display: 'none'
      }
    },
    header: {
      borderBottom: '3px solid var(--primary)',
      paddingBottom: '3rem',
      marginBottom: '4rem',
      paddingTop: '3rem',
      paddingLeft: '3rem',
      paddingRight: '3rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.5rem',
      '@media (max-width: 768px)': {
        paddingTop: '2rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        paddingBottom: '2rem',
        marginBottom: '2rem',
      }
    },
    name: {
      fontFamily: "'Lora', serif",
      fontSize: '3.5rem',
      fontWeight: '700',
      color: 'var(--text-primary)',
      marginBottom: '0.5rem',
      lineHeight: '1',
      letterSpacing: '-0.02em',
      whiteSpace: 'nowrap',
      '@media (max-width: 900px)': {
        fontSize: '2.5rem',
        whiteSpace: 'normal',
      }
    },
    role: {
      fontSize: '1.4rem',
      color: 'var(--text-secondary)',
      fontWeight: '400',
      letterSpacing: '0.05em',
      fontFamily: "'Lato', sans-serif",
      marginBottom: '0.5rem',
    },
    contactColumn: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '2rem',
      alignItems: 'center',
      fontSize: '1rem',
      color: 'var(--text-secondary)',
      width: '100%',
    },
    contactRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
    },
    contactLink: {
      color: 'var(--text-secondary)',
      textDecoration: 'none',
      transition: 'color 0.2s',
      ':hover': {
        color: 'var(--primary)',
      }
    },
    summarySection: {
      marginBottom: '3rem',
      paddingBottom: '2rem',
      borderBottom: '1px solid var(--border)',
    },
    summaryText: {
      fontSize: '1.05rem',
      lineHeight: '1.7',
      color: 'var(--text-primary)',
      fontFamily: "'Lora', serif",
      fontStyle: 'italic',
      maxWidth: '90%',
    },
    mainContent: {
      display: 'flex',
      gap: '4rem',
      '@media (max-width: 768px)': {
        flexDirection: 'column',
        gap: '3rem'
      }
    },
    leftColumn: {
      flex: '1.8',
    },
    rightColumn: {
      flex: '1',
    },
    sectionTitle: {
      fontSize: '1.2rem',
      fontWeight: '800',
      textTransform: 'uppercase',
      letterSpacing: '0.15em',
      color: 'var(--primary)',
      marginBottom: '2rem',
      borderBottom: '1px solid var(--border)',
      paddingBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    sectionIcon: {
      color: 'var(--primary)',
      fontSize: '1rem',
      opacity: 0.8
    },
    timelineItem: {
      position: 'relative',
      marginBottom: '2.5rem',
      ':last-child': {
        marginBottom: 0,
      }
    },
    itemHeader: {
      marginBottom: '0.5rem',
    },
    itemTitle: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: 'var(--text-primary)',
      fontFamily: "'Lora', serif",
      lineHeight: '1.3',
    },
    itemSubtitle: {
      fontSize: '1rem',
      color: 'var(--text-secondary)',
      fontWeight: '600',
      marginTop: '0.25rem',
    },
    itemMeta: {
      fontSize: '0.9rem',
      color: 'var(--text-muted)',
      marginTop: '0.25rem',
      fontWeight: '500',
      fontStyle: 'italic',
    },
    itemDescription: {
      fontSize: '0.95rem',
      lineHeight: '1.6',
      color: 'var(--text-secondary)',
      marginTop: '0.75rem',
    },
    skillCategory: {
      marginBottom: '1.5rem',
    },
    skillCategoryTitle: {
      fontSize: '0.9rem',
      fontWeight: '700',
      color: 'var(--text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '0.5rem',
    },
    skillTag: {
      display: 'inline-block',
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      backgroundColor: 'var(--bg-main)',
      padding: '0.3rem 0.75rem',
      borderRadius: '4px',
      marginRight: '0.5rem',
      marginBottom: '0.5rem',
      fontWeight: '600',
      border: '1px solid var(--border)',
    },
    downloadBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.5rem 1.5rem',
      backgroundColor: 'var(--primary)',
      color: 'white',
      borderRadius: '50px',
      textDecoration: 'none',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      ':hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        opacity: 0.95,
        color: 'white'
      },
      '@media print': {
        display: 'none'
      }
    }
  });

  return (
    <section id="resume" className={css(styles.sectionWrapper)}>

      <div className={css(styles.titleBox)}>
        <h2 className={css(styles.mainTitle)}>Resume</h2>
        <div className={css(styles.titleUnderline)}></div>
      </div>

      <div className={`${css(styles.paperContainer)} resume-paper`}>
        <div className={css(styles.topLabel)}>Curriculum Vitae</div>

        <div className={css(styles.header)}>
          <div className={css(styles.nameColumn)}>
            <h1 className={css(styles.name)}>Mohammad Sayem Chowdhury</h1>
            <div className={css(styles.role)}>Research Assistant · M.Sc. Computer Science</div>
          </div>

          <div className={css(styles.contactColumn)}>
            <div className={css(styles.contactRow)}>
              <FontAwesomeIcon icon={faEnvelope} style={{ width: '16px', color: 'var(--primary)' }} />
              <a href="mailto:m.sayem.c@gmail.com" className={css(styles.contactLink)}>m.sayem.c@gmail.com</a>
            </div>
            <div className={css(styles.contactRow)}>
              <FontAwesomeIcon icon={faLinkedin} style={{ width: '16px', color: 'var(--primary)' }} />
              <a href="https://linkedin.com/in/mohammad-sayem-chowdhury-a3384b80" target="_blank" rel="noreferrer" className={css(styles.contactLink)}>LinkedIn</a>
            </div>
            <div className={css(styles.contactRow)}>
              <FontAwesomeIcon icon={faGoogleScholar} style={{ width: '16px', color: 'var(--primary)' }} />
              <a href="https://scholar.google.com/citations?user=wDPGD9MAAAAJ" target="_blank" rel="noreferrer" className={css(styles.contactLink)}>Google Scholar</a>
            </div>
            <div className={css(styles.contactRow)}>
              <FontAwesomeIcon icon={faMapMarkerAlt} style={{ width: '16px', color: 'var(--primary)' }} />
              <span>Chattogram, Bangladesh</span>
            </div>
            <div className={css(styles.contactRow)}>
               <a href="assets/cv/Mohammad_Sayem_Chowdhury_CV.pdf" download className={css(styles.downloadBtn)}>
                <FontAwesomeIcon icon={faDownload} style={{ marginRight: '0.5rem' }} /> Download PDF
              </a>
            </div>
          </div>
        </div>

        <div className={css(styles.summarySection)}>
          <p className={css(styles.summaryText)}>
            AI researcher focused on interpretable deep learning for medical imaging and safety-critical vision systems. Experienced in developing and evaluating deep learning models under data scarcity, with an emphasis on explainability, transfer learning, and attribution-based validation.
          </p>
        </div>

        <div className={css(styles.mainContent)}>

          <div className={css(styles.leftColumn)}>

            <div className={css(styles.contentGroup)}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faBriefcase} className={css(styles.sectionIcon)} />
                Professional Experience
              </h3>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemHeader)}>
                  <div className={css(styles.itemTitle)}>Research Assistant</div>
                  <div className={css(styles.itemSubtitle)}>Advanced Machine Intelligence Research Lab - AMIR Lab</div>
                  <div className={css(styles.itemMeta)}>Jan 2024 - Present</div>
                </div>
                <ul className={css(styles.itemDescription)} style={{ paddingLeft: '1.2rem', margin: 0 }}>
                  <li style={{ marginBottom: '0.5rem' }}>Conduct research on interpretable deep learning and transfer learning for medical imaging and safety-critical vision tasks.</li>
                  <li style={{ marginBottom: '0.5rem' }}>Design and evaluate deep learning pipelines for disease diagnosis and object detection under limited and imbalanced data.</li>
                  <li>Integrate explainable AI techniques to analyze model reasoning and support attribution-based validation.</li>
                </ul>
              </div>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemHeader)}>
                  <div className={css(styles.itemTitle)}>Intern Student</div>
                  <div className={css(styles.itemSubtitle)}>American International University-Bangladesh</div>
                  <div className={css(styles.itemMeta)}>Jan 2022 - Apr 2022</div>
                </div>
                <ul className={css(styles.itemDescription)} style={{ paddingLeft: '1.2rem', margin: 0 }}>
                  <li style={{ marginBottom: '0.5rem' }}>Collaborated with faculty on course material development for 100+ students.</li>
                  <li>Provided academic support and facilitated technical discussions.</li>
                </ul>
              </div>
            </div>

            <div className={css(styles.contentGroup)} style={{ marginTop: '4rem' }}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faBook} className={css(styles.sectionIcon)} />
                Selected Publications
              </h3>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemTitle)} style={{ fontSize: '1.1rem' }}>Leveraging Deep Neural Networks to Uncover Unprecedented Levels of Precision in the Diagnosis of Hair and Scalp Disorders</div>
                <div className={css(styles.itemSubtitle)}>Skin Research and Technology (Wiley), 2024</div>
                <div className={css(styles.itemDescription)}>M.Sc. Thesis</div>
              </div>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemTitle)} style={{ fontSize: '1.1rem' }}>Optimized Approaches for Real-Life Sentiment Analysis in E-Commerce</div>
                <div className={css(styles.itemSubtitle)}>Springer Nature (Book Chapter), 2025</div>
                <div className={css(styles.itemDescription)}>B.Sc. Thesis · Lecture Notes in Networks and Systems</div>
              </div>
            </div>

            <div className={css(styles.contentGroup)} style={{ marginTop: '4rem' }}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faLayerGroup} className={css(styles.sectionIcon)} />
                Research Interests
              </h3>
              <div className={css(styles.skillCategory)}>
                <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                  {[
                    'Explainable AI (XAI) & Interpretability',
                    'Concept Bottleneck Models (CBMs)',
                    'Uncertainty Quantification',
                    'Human-in-the-loop Machine Learning'
                  ].map(interest => (
                    <span key={interest} className={css(styles.skillTag)} style={{ backgroundColor: 'var(--primary)', color: 'white' }}>{interest}</span>
                  ))}
                </div>
              </div>
            </div>

          </div>

          <div className={css(styles.rightColumn)}>

            <div className={css(styles.contentGroup)}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faGraduationCap} className={css(styles.sectionIcon)} />
                Education
              </h3>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemTitle)}>M.Sc. in Computer Science</div>
                <div className={css(styles.itemSubtitle)}>American International University-Bangladesh</div>
                <div className={css(styles.itemMeta)}>2023</div>
                <div className={css(styles.itemDescription)}>
                  <strong>Major:</strong> Intelligent Systems<br/>
                  CGPA: 3.85 / 4.00<br/>
                  <span style={{ color: 'var(--primary)', fontWeight: '600' }}>Magna Cum Laude</span>
                </div>
              </div>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemTitle)}>B.Sc. in Computer Science and Engineering</div>
                <div className={css(styles.itemSubtitle)}>American International University-Bangladesh</div>
                <div className={css(styles.itemMeta)}>2018 - 2022</div>
                <div className={css(styles.itemDescription)}>
                  <strong>Major:</strong> Information Systems<br/>
                  CGPA: 3.77 / 4.00
                </div>
              </div>
            </div>

            <div className={css(styles.contentGroup)} style={{ marginTop: '4rem' }}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faUsers} className={css(styles.sectionIcon)} />
                Leadership
              </h3>

              <div className={css(styles.timelineItem)}>
                <div className={css(styles.itemTitle)}>Volunteer & Leadership</div>
                <div className={css(styles.itemSubtitle)}>IEEE CS BDC & AIUB Student Branch</div>
                <div className={css(styles.itemMeta)}>2020 – 2022</div>
                <div className={css(styles.itemDescription)}>
                  Led development of Team Spark website. Organized Summer Symposium 2022.
                </div>
              </div>
            </div>



            <div className={css(styles.contentGroup)} style={{ marginTop: '4rem' }}>
              <h3 className={css(styles.sectionTitle)}>
                <FontAwesomeIcon icon={faCode} className={css(styles.sectionIcon)} />
                Skills & Methods
              </h3>

              {skillsAndMethods.map(category => (
                <div key={category.id} className={css(styles.skillCategory)}>
                  <div className={css(styles.skillCategoryTitle)}>{category.title}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                    {category.items.map(skill => (
                      <span key={skill} className={css(styles.skillTag)}>{skill}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

      </div>

    </section>
  );
}

export default Resume;
